import { useEffect, useState } from "react";
import { api, API_BASE, formatMoney, formatDate, todayISO, openDownload } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, CreditCard, Wallet, FileDown, FileText, Calculator, TrendingDown, CalendarCheck, Check, Pencil } from "lucide-react";

const TYPE_LABEL = {
  commercial: "Ticari Kredi",
  consumer: "İhtiyaç Kredisi",
  vehicle: "Taşıt Kredisi",
  mortgage: "Konut Kredisi",
  credit_line: "Rotatif / KMH",
};

export default function Loans() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [payAmt, setPayAmt] = useState(0);
  const [payBank, setPayBank] = useState("");
  const [simOpen, setSimOpen] = useState(null);
  const [simMode, setSimMode] = useState("extra_monthly");
  const [simExtra, setSimExtra] = useState("");
  const [simLump, setSimLump] = useState("");
  const [simLumpMonth, setSimLumpMonth] = useState(1);
  const [simTarget, setSimTarget] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [schedOpen, setSchedOpen] = useState(null);
  const [schedRows, setSchedRows] = useState([]);
  const [schedLoading, setSchedLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", bank_name: "", loan_type: "commercial",
    principal: "", currency: "TRY", interest_rate: "",
    start_date: todayISO(), term_months: 12, monthly_payment: "", notes: "",
  });

  const load = async () => {
    const [l, b] = await Promise.all([api.get("/loans"), api.get("/bank-accounts")]);
    setItems(l.data); setBanks(b.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/loans", {
        ...form, principal: Number(form.principal), interest_rate: Number(form.interest_rate),
        term_months: Number(form.term_months), monthly_payment: Number(form.monthly_payment),
      });
      toast.success("Kredi eklendi");
      setOpen(false);
      setForm({ ...form, name: "", principal: "", monthly_payment: "", interest_rate: "" });
      load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const pay = async () => {
    try {
      await api.post(`/loans/${payOpen.id}/pay`, { amount: payAmt, bank_account_id: payBank || null, record_expense: true });
      toast.success("Taksit ödendi ve gider olarak kaydedildi");
      setPayOpen(null); load();
    } catch { toast.error("Ödeme başarısız"); }
  };

  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/loans/${id}`); load(); };

  const runSimulation = async () => {
    if (!simOpen) return;
    setSimLoading(true);
    try {
      const payload = { mode: simMode };
      if (simMode === "extra_monthly") payload.extra_monthly = Number(simExtra) || 0;
      if (simMode === "lump_sum") { payload.lump_sum = Number(simLump) || 0; payload.lump_sum_month = Number(simLumpMonth) || 1; }
      if (simMode === "target_months") payload.target_months = Number(simTarget) || 0;
      const { data } = await api.post(`/loans/${simOpen.id}/early-payoff`, payload);
      setSimResult(data);
    } catch (e) {
      toast.error("Simülasyon başarısız");
    } finally {
      setSimLoading(false);
    }
  };

  const openSim = (loan) => {
    setSimOpen(loan);
    setSimMode("extra_monthly");
    setSimExtra("");
    setSimLump("");
    setSimLumpMonth(1);
    setSimTarget(Math.max(1, Math.floor(loan.term_months / 2)));
    setSimResult(null);
  };

  const closeSim = () => { setSimOpen(null); setSimResult(null); };

  const openSched = async (loan) => {
    setSchedOpen(loan);
    setSchedLoading(true);
    try {
      const { data } = await api.get(`/loans/${loan.id}/amortization`);
      // If no custom schedule, pre-fill with auto rows so user can edit
      const rows = (data.rows || []).map(r => ({
        no: r.no,
        date: r.date,
        payment: r.payment,
        principal: r.principal || 0,
        interest: r.interest || 0,
        paid: r.paid || false,
        note: r.note || "",
      }));
      setSchedRows(rows);
    } catch {
      toast.error("Plan yüklenemedi");
    } finally {
      setSchedLoading(false);
    }
  };

  const schedAdd = () => {
    const last = schedRows[schedRows.length - 1];
    let nextDate = todayISO();
    if (last?.date) {
      const d = new Date(last.date);
      d.setMonth(d.getMonth() + 1);
      nextDate = d.toISOString().slice(0, 10);
    }
    setSchedRows([...schedRows, {
      no: schedRows.length + 1, date: nextDate,
      payment: 0, principal: 0, interest: 0, paid: false, note: "",
    }]);
  };

  const schedUpdate = (idx, field, value) => {
    const copy = [...schedRows];
    copy[idx] = { ...copy[idx], [field]: value };
    setSchedRows(copy);
  };

  const schedDel = (idx) => {
    const copy = schedRows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, no: i + 1 }));
    setSchedRows(copy);
  };

  const schedSave = async () => {
    if (!schedOpen) return;
    setSchedLoading(true);
    try {
      await api.put(`/loans/${schedOpen.id}/schedule`, { schedule: schedRows });
      toast.success("Ödeme planı kaydedildi");
      setSchedOpen(null);
      load();
    } catch {
      toast.error("Kayıt başarısız");
    } finally {
      setSchedLoading(false);
    }
  };

  const schedClear = async () => {
    if (!schedOpen) return;
    if (!window.confirm("Manuel plan silinip otomatik amortisman hesabına dönülsün mü?")) return;
    setSchedLoading(true);
    try {
      await api.put(`/loans/${schedOpen.id}/schedule`, { schedule: null });
      toast.success("Otomatik hesaplamaya döndü");
      setSchedOpen(null);
      load();
    } catch {
      toast.error("İşlem başarısız");
    } finally {
      setSchedLoading(false);
    }
  };

  const downloadSimPdf = () => {
    if (!simOpen || !simResult) return;
    const t = localStorage.getItem("madymark_token");
    const params = new URLSearchParams({ mode: simMode, auth: t });
    if (simMode === "extra_monthly") params.set("extra_monthly", simResult.inputs.extra_monthly);
    if (simMode === "lump_sum") { params.set("lump_sum", simResult.inputs.lump_sum); params.set("lump_sum_month", simResult.inputs.lump_sum_month); }
    if (simMode === "target_months") params.set("target_months", simResult.inputs.target_months);
    window.open(`${API_BASE}/api/loans/${simOpen.id}/early-payoff.pdf?${params.toString()}`, "_blank");
  };

  const totalRemaining = items.reduce((s, l) => s + l.remaining_balance, 0);
  const totalMonthly = items.reduce((s, l) => s + l.monthly_payment, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">Bankacılık</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">Kredi Takibi</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="loan-xlsx" onClick={() => openDownload(`/api/export/loans.xlsx`)} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 text-slate-700 hover:text-emerald-600 font-bold transition">
            <FileDown className="w-4 h-4" /> Excel
          </button>
          <button data-testid="add-loan" onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20"><Plus className="w-4 h-4" /> Yeni Kredi</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-rose-600 font-bold">Toplam Kalan Borç</div>
            <CreditCard className="w-4 h-4 text-rose-500" />
          </div>
          <div className="font-mono-num text-3xl font-extrabold text-rose-600 mt-3">{formatMoney(totalRemaining, "TRY")}</div>
          <div className="text-xs text-slate-500 mt-1">{items.length} aktif kredi</div>
        </div>
        <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-amber-600 font-bold">Aylık Toplam Taksit</div>
            <Wallet className="w-4 h-4 text-amber-500" />
          </div>
          <div className="font-mono-num text-3xl font-extrabold text-amber-600 mt-3">{formatMoney(totalMonthly, "TRY")}</div>
          <div className="text-xs text-slate-500 mt-1">Nakit akış planlaması için</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.length === 0 && <div className="col-span-full text-slate-400 text-center py-16">Kredi eklenmedi</div>}
        {items.map(l => {
          const paidPct = l.principal ? Math.min(100, (1 - l.remaining_balance / l.principal) * 100) : 0;
          const paidAmt = l.principal - l.remaining_balance;
          return (
            <div key={l.id} className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 relative overflow-hidden group">
              <button onClick={() => del(l.id)} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E2A9C] to-[#29B7E3] grid place-items-center text-white">
                  <CreditCard className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-lg text-slate-900 dark:text-slate-100">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.bank_name} · {TYPE_LABEL[l.loan_type] || l.loan_type} · %{l.interest_rate} yıllık</div>
                </div>
              </div>
              <div className="mt-5">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Ödenen: <span className="font-mono-num font-bold text-emerald-600">{formatMoney(paidAmt, l.currency)}</span></span>
                  <span>Kalan: <span className="font-mono-num font-bold text-rose-600">{formatMoney(l.remaining_balance, l.currency)}</span></span>
                </div>
                <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${paidPct}%` }} />
                </div>
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-400 mt-1 font-bold">
                  <span>Ana Para: {formatMoney(l.principal, l.currency)}</span>
                  <span>{paidPct.toFixed(1)}% ödendi</span>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Aylık Taksit</div>
                  <div className="font-mono-num font-bold text-slate-800 dark:text-slate-200">{formatMoney(l.monthly_payment, l.currency)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ödendi</div>
                  <div className="font-mono-num font-bold text-slate-800 dark:text-slate-200">{l.installments_paid} / {l.term_months}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Başlangıç</div>
                  <div className="font-mono-num text-slate-800 dark:text-slate-200 text-xs">{formatDate(l.start_date)}</div>
                </div>
              </div>
              <button data-testid={`pay-${l.id}`} onClick={() => { setPayOpen(l); setPayAmt(l.monthly_payment); }} className="w-full mt-4 py-2.5 rounded-lg bg-[#1E2A9C] text-white text-sm font-bold hover:bg-[#121A68]">
                Taksit Öde
              </button>
              <button data-testid={`amort-${l.id}`} onClick={() => openDownload(`/api/loans/${l.id}/amortization.pdf`)} className="w-full mt-2 py-2 rounded-lg border border-slate-200 text-slate-700 text-xs font-bold hover:border-[#1E2A9C] hover:text-[#1E2A9C] flex items-center justify-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Amortisman Planı (PDF)
              </button>
              <button data-testid={`sim-${l.id}`} onClick={() => openSim(l)} className="w-full mt-2 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-xs font-bold hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 flex items-center justify-center gap-1">
                <Calculator className="w-3.5 h-3.5" /> Erken Kapatma Simülasyonu
              </button>
              <button data-testid={`sched-${l.id}`} onClick={() => openSched(l)} className="w-full mt-2 py-2 rounded-lg border border-amber-200 text-amber-700 text-xs font-bold hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30 flex items-center justify-center gap-1">
                <CalendarCheck className="w-3.5 h-3.5" /> Ödeme Planı (Manuel)
              </button>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl p-8 relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6">Yeni Kredi</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Kredi Adı</label>
                <input required data-testid="loan-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Örn. Ticari Kredi 1" className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Banka</label>
                <input required data-testid="loan-bank" value={form.bank_name} onChange={(e) => setForm({...form, bank_name: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Kredi Tipi</label>
                <select data-testid="loan-type" value={form.loan_type} onChange={(e) => setForm({...form, loan_type: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ana Para</label>
                <input required type="number" step="0.01" data-testid="loan-principal" value={form.principal} onChange={(e) => setForm({...form, principal: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                <select data-testid="loan-currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Faiz Oranı % (Yıllık)</label>
                <input type="number" step="0.01" data-testid="loan-rate" value={form.interest_rate} onChange={(e) => setForm({...form, interest_rate: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Vade (Ay)</label>
                <input type="number" data-testid="loan-term" value={form.term_months} onChange={(e) => setForm({...form, term_months: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Aylık Taksit</label>
                <input required type="number" step="0.01" data-testid="loan-monthly" value={form.monthly_payment} onChange={(e) => setForm({...form, monthly_payment: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Başlangıç</label>
                <input type="date" data-testid="loan-start" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Notlar</label>
                <textarea data-testid="loan-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
            </div>
            <button data-testid="loan-submit" className="w-full mt-6 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </form>
        </div>
      )}

      {payOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 relative">
            <button onClick={() => setPayOpen(null)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-2">Taksit Ödemesi</div>
            <p className="text-sm text-slate-500 mb-5">{payOpen.name} · Kalan: <b>{formatMoney(payOpen.remaining_balance, payOpen.currency)}</b></p>
            <div className="space-y-3">
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tutar</label>
                <input required type="number" step="0.01" data-testid="pay-amount" value={payAmt} onChange={(e) => setPayAmt(Number(e.target.value))} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Banka Hesabı</label>
                <select data-testid="pay-bank" value={payBank} onChange={(e) => setPayBank(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  <option value="">-</option>
                  {banks.filter(b => b.currency === payOpen.currency).map(b => <option key={b.id} value={b.id}>{b.bank_name} ({b.name})</option>)}
                </select></div>
              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                💡 Ödeme otomatik olarak gider işlemi olarak kaydedilir ve banka bakiyesi güncellenir.
              </div>
              <button data-testid="pay-submit" onClick={pay} className="w-full mt-2 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Öde ve Gider Kaydet</button>
            </div>
          </div>
        </div>
      )}
      {simOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
          <div data-testid="sim-modal" className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-3xl p-8 relative my-8">
            <button onClick={closeSim} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="flex items-start gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white">
                <Calculator className="w-6 h-6" />
              </div>
              <div>
                <div className="font-display text-2xl font-extrabold text-slate-900 dark:text-slate-100">Erken Kapatma Simülasyonu</div>
                <div className="text-sm text-slate-500">{simOpen.name} · {simOpen.bank_name} · {formatMoney(simOpen.remaining_balance, simOpen.currency)} kalan</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { k: "extra_monthly", label: "Aylık Ek Ödeme", desc: "Her ay taksite ek" },
                { k: "lump_sum", label: "Tek Seferlik", desc: "Bir kerelik ödeme" },
                { k: "target_months", label: "Hedef Vade", desc: "Kaç ayda kapat" },
              ].map(o => (
                <button key={o.k} data-testid={`sim-mode-${o.k}`} onClick={() => { setSimMode(o.k); setSimResult(null); }}
                  className={`p-4 rounded-xl border-2 text-left transition ${simMode === o.k ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-200 dark:border-slate-700 hover:border-slate-400"}`}>
                  <div className="font-bold text-sm text-slate-900 dark:text-slate-100">{o.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{o.desc}</div>
                </button>
              ))}
            </div>

            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-5 mb-5">
              {simMode === "extra_monthly" && (
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Aylık Ek Ödeme Tutarı ({simOpen.currency})</label>
                  <input data-testid="sim-extra" type="number" step="0.01" value={simExtra} onChange={(e) => setSimExtra(e.target.value)}
                    placeholder="Örn. 5000" className="w-full mt-2 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg font-mono-num text-lg dark:bg-slate-900" />
                  <p className="text-xs text-slate-500 mt-2">Normal aylık taksitinize ek olarak her ay ödeyeceğiniz tutar</p>
                </div>
              )}
              {simMode === "lump_sum" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tek Seferlik Tutar ({simOpen.currency})</label>
                    <input data-testid="sim-lump" type="number" step="0.01" value={simLump} onChange={(e) => setSimLump(e.target.value)}
                      placeholder="Örn. 100000" className="w-full mt-2 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg font-mono-num text-lg dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Kaçıncı Ayda Ödenecek</label>
                    <input data-testid="sim-lumpmonth" type="number" min="1" max={simOpen.term_months} value={simLumpMonth} onChange={(e) => setSimLumpMonth(e.target.value)}
                      className="w-full mt-2 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg font-mono-num text-lg dark:bg-slate-900" />
                  </div>
                </div>
              )}
              {simMode === "target_months" && (
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Hedef Vade (Ay)</label>
                  <input data-testid="sim-target" type="number" min="1" max={simOpen.term_months - 1} value={simTarget} onChange={(e) => setSimTarget(e.target.value)}
                    className="w-full mt-2 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg font-mono-num text-lg dark:bg-slate-900" />
                  <p className="text-xs text-slate-500 mt-2">Krediyi kaç ayda kapatmak istiyorsunuz? (Orijinal: {simOpen.term_months} ay)</p>
                </div>
              )}
              <button data-testid="sim-run" onClick={runSimulation} disabled={simLoading}
                className="w-full mt-4 py-3 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50">
                {simLoading ? "Hesaplanıyor..." : "Simülasyonu Çalıştır"}
              </button>
            </div>

            {simResult && (
              <div data-testid="sim-result" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Orijinal Plan</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Vade</span><span className="font-mono-num font-bold">{simResult.original.months} ay</span></div>
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Aylık Taksit</span><span className="font-mono-num font-bold">{formatMoney(simResult.original.monthly_payment, simOpen.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Toplam Faiz</span><span className="font-mono-num font-bold text-rose-600">{formatMoney(simResult.original.total_interest, simOpen.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Toplam Ödeme</span><span className="font-mono-num font-bold">{formatMoney(simResult.original.total_paid, simOpen.currency)}</span></div>
                    </div>
                  </div>
                  <div className="p-5 rounded-xl border-2 border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-bold mb-2">Yeni Plan (Ek Ödemeli)</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Vade</span><span className="font-mono-num font-bold text-emerald-700 dark:text-emerald-400">{simResult.new_plan.months} ay</span></div>
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Aylık Taksit</span><span className="font-mono-num font-bold">{formatMoney(simResult.new_plan.monthly_payment, simOpen.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Toplam Faiz</span><span className="font-mono-num font-bold text-emerald-700 dark:text-emerald-400">{formatMoney(simResult.new_plan.total_interest, simOpen.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-400">Toplam Ödeme</span><span className="font-mono-num font-bold">{formatMoney(simResult.new_plan.total_paid, simOpen.currency)}</span></div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <TrendingDown className="w-6 h-6" />
                    <div className="font-display font-extrabold text-xl">Kazancınız</div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Vade Kısalması</div>
                      <div className="font-mono-num text-2xl font-extrabold mt-1">{simResult.savings.months_saved} ay</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Faiz Tasarrufu</div>
                      <div className="font-mono-num text-2xl font-extrabold mt-1">{formatMoney(simResult.savings.interest_saved, simOpen.currency)}</div>
                      <div className="text-xs opacity-80">%{simResult.savings.interest_saved_pct} tasarruf</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">
                        {simMode === "target_months" ? "Gerekli Aylık Ek" : "Toplam Fark"}
                      </div>
                      <div className="font-mono-num text-2xl font-extrabold mt-1">
                        {simMode === "target_months"
                          ? formatMoney(simResult.inputs.computed_extra || 0, simOpen.currency)
                          : formatMoney(Math.abs(simResult.savings.total_saved), simOpen.currency)}
                      </div>
                    </div>
                  </div>
                </div>

                <button data-testid="sim-pdf" onClick={downloadSimPdf}
                  className="w-full py-3 rounded-lg border-2 border-[#1E2A9C] text-[#1E2A9C] dark:text-blue-300 font-bold hover:bg-[#1E2A9C] hover:text-white transition flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4" /> Karşılaştırma Raporunu PDF Olarak İndir
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {schedOpen && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col" data-testid="sched-modal">
          <div className="p-5 border-b flex items-center justify-between bg-white flex-shrink-0">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{schedOpen.name} · {schedOpen.bank_name}</div>
              <div className="font-display text-xl font-extrabold">Manuel Ödeme Planı</div>
              <div className="text-xs text-slate-500 mt-1">Ödemesiz dönem veya değişken taksitli krediler için her satırı istediğiniz gibi düzenleyin.</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={schedClear} className="px-4 py-2 text-sm rounded-lg border border-rose-200 text-rose-700 font-bold hover:bg-rose-50">Manuel Planı Sil</button>
              <button onClick={schedAdd} data-testid="sched-add" className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 font-bold hover:border-[#1E2A9C] hover:text-[#1E2A9C] flex items-center gap-1"><Plus className="w-4 h-4" /> Taksit Ekle</button>
              <button onClick={schedSave} disabled={schedLoading} data-testid="sched-save" className="px-5 py-2 text-sm rounded-lg bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] disabled:opacity-50 flex items-center gap-1"><Check className="w-4 h-4" /> Kaydet</button>
              <button onClick={() => setSchedOpen(null)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            {schedLoading && schedRows.length === 0 ? (
              <div className="text-center text-slate-500 py-20">Yükleniyor…</div>
            ) : schedRows.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-slate-400 mb-4">Henüz taksit yok</div>
                <button onClick={schedAdd} className="px-5 py-2.5 rounded-lg bg-[#1E2A9C] text-white font-bold">İlk Taksiti Ekle</button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase font-bold text-slate-500 tracking-widest border-b">
                    <th className="text-left py-3 px-2 w-14">#</th>
                    <th className="text-left py-3 px-2 w-40">Tarih</th>
                    <th className="text-right py-3 px-2 w-32">Taksit</th>
                    <th className="text-right py-3 px-2 w-32">Anapara</th>
                    <th className="text-right py-3 px-2 w-32">Faiz</th>
                    <th className="text-left py-3 px-2">Not (ör. ödemesiz dönem)</th>
                    <th className="text-center py-3 px-2 w-20">Ödendi</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {schedRows.map((r, i) => (
                    <tr key={i} className={`border-b hover:bg-slate-50 ${r.paid ? "bg-emerald-50/40" : ""}`}>
                      <td className="py-2 px-2 font-mono-num text-slate-500">{r.no}</td>
                      <td className="py-2 px-2">
                        <input type="date" data-testid={`sched-date-${i}`} value={r.date} onChange={(e) => schedUpdate(i, "date", e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" step="0.01" data-testid={`sched-pay-${i}`} value={r.payment} onChange={(e) => schedUpdate(i, "payment", Number(e.target.value))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-right font-mono-num text-sm" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" step="0.01" value={r.principal} onChange={(e) => schedUpdate(i, "principal", Number(e.target.value))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-right font-mono-num text-sm" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" step="0.01" value={r.interest} onChange={(e) => schedUpdate(i, "interest", Number(e.target.value))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-right font-mono-num text-sm" />
                      </td>
                      <td className="py-2 px-2">
                        <input value={r.note} placeholder="Örn. ödemesiz dönem" onChange={(e) => schedUpdate(i, "note", e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm" />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <input type="checkbox" checked={r.paid} onChange={(e) => schedUpdate(i, "paid", e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                      </td>
                      <td className="py-2 px-2">
                        <button onClick={() => schedDel(i)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-slate-50">
                    <td colSpan={2} className="py-3 px-2 text-right text-slate-600 uppercase text-xs tracking-widest">Toplam</td>
                    <td className="py-3 px-2 text-right font-mono-num">{formatMoney(schedRows.reduce((s, r) => s + Number(r.payment || 0), 0), schedOpen.currency)}</td>
                    <td className="py-3 px-2 text-right font-mono-num">{formatMoney(schedRows.reduce((s, r) => s + Number(r.principal || 0), 0), schedOpen.currency)}</td>
                    <td className="py-3 px-2 text-right font-mono-num">{formatMoney(schedRows.reduce((s, r) => s + Number(r.interest || 0), 0), schedOpen.currency)}</td>
                    <td colSpan={3} className="py-3 px-2 text-xs text-slate-500">
                      {schedRows.filter(r => r.paid).length} / {schedRows.length} ödendi
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
